import { ROTATION_DIRECTION } from '../config';

export class Rotate {
    #currentAngle = 0;
    #rotationStep = 90;
    #rotationDirection = ROTATION_DIRECTION.COUNTERCLOCKWISE;
    #element;
    #defaultScale = 1;
    #resizeObserver = null;

    #zoomWraper;
    constructor({ elementId, zoomWraper }) {
        this.#element = document.getElementById(elementId);
        this.#zoomWraper = zoomWraper;
    }

    rotate = (reset) => {
        if (!this.#element) return;

        const newAngle = reset ? 0 : this.#calculateNewAngle();
        this.#currentAngle = newAngle;
        this.#applyTransform();

        // Every rotation resets digital zoom/pan so the whole rotated frame fits the viewport
        this.#zoomWraper?.resetTransform?.(0);

        if (this.#isSideways()) this.#observeResize();
        else this.#stopObservingResize();
    };

    // The same element can get a new picture (image navigation, refreshed S3 link) and a new Rotate starting at 0deg,
    // so the old rotation must not stay on it
    destroy() {
        this.#stopObservingResize();
        if (this.#element) this.#element.style.transform = '';
    }

    #applyTransform = () => {
        if (!this.#element) return;
        const scale = this.#isSideways() ? this.#calculateFitScale() : this.#defaultScale;
        this.#element.style.transform = `rotate(${this.#currentAngle}deg) scale(${scale})`;
    };

    #isSideways() {
        return this.#currentAngle === 90 || this.#currentAngle === 270;
    }

    // Scale that fits the rotated picture inside the visible viewport (the zoom wrapper).
    // Computed from layout sizes on every call, so it does not depend on the size the element had when it was created.
    #calculateFitScale() {
        const { width: contentWidth, height: contentHeight } = this.#getRenderedContentSize();
        const viewport = this.#getViewport();
        const viewportWidth = viewport?.clientWidth;
        const viewportHeight = viewport?.clientHeight;
        if (!contentWidth || !contentHeight || !viewportWidth || !viewportHeight) return this.#defaultScale;

        // After a 90/270 rotation the content width runs vertically and the height horizontally
        return Math.min(viewportWidth / contentHeight, viewportHeight / contentWidth);
    }

    // Size of the picture actually painted inside the element (object-fit: contain letterboxes it)
    #getRenderedContentSize() {
        const element = this.#element;
        const boxWidth = element.clientWidth;
        const boxHeight = element.clientHeight;
        const intrinsicWidth = element.videoWidth || element.naturalWidth;
        const intrinsicHeight = element.videoHeight || element.naturalHeight;
        if (!intrinsicWidth || !intrinsicHeight || !boxWidth || !boxHeight) return { width: boxWidth, height: boxHeight };
        const { objectFit } = window.getComputedStyle(element);
        const isLetterboxed = element.tagName === 'VIDEO' ? !['fill', 'cover'].includes(objectFit) : objectFit === 'contain';
        if (!isLetterboxed) return { width: boxWidth, height: boxHeight };

        const fit = Math.min(boxWidth / intrinsicWidth, boxHeight / intrinsicHeight);
        return { width: intrinsicWidth * fit, height: intrinsicHeight * fit };
    }

    #getViewport() {
        return this.#zoomWraper?.instance?.wrapperComponent ?? this.#element.parentElement;
    }

    // Tile expand/collapse, fullscreen, window resize or a stream resolution change must refit the rotated picture
    #observeResize() {
        if (this.#resizeObserver || typeof ResizeObserver === 'undefined') return;
        this.#resizeObserver = new ResizeObserver(this.#applyTransform);
        const viewport = this.#getViewport();
        if (viewport) this.#resizeObserver.observe(viewport);
        this.#resizeObserver.observe(this.#element);
        this.#element.addEventListener('resize', this.#applyTransform);
    }

    #stopObservingResize() {
        if (!this.#resizeObserver) return;
        this.#resizeObserver.disconnect();
        this.#resizeObserver = null;
        this.#element?.removeEventListener('resize', this.#applyTransform);
    }

    #calculateNewAngle() {
        let newAngle = this.#currentAngle;
        const isClockWise = this.#rotationDirection === ROTATION_DIRECTION.CLOCKWISE;
        if (isClockWise) {
            newAngle += this.#rotationStep;
            if (newAngle >= 360) {
                newAngle -= 360;
            }
        } else {
            newAngle -= this.#rotationStep;
            if (newAngle < 0) {
                newAngle += 360;
            }
        }

        return newAngle;
    }

    get rotationAngle() {
        return this.#currentAngle;
    }
}
